import { value } from '@takeshape/util';
import { hasValidEndpoint } from "../util/config.js";
import { getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
const title = 'Sanity';
const serviceProvider = {
    name: 'sanity',
    title,
    description: 'Sanity is a headless content management system.',
    categories: ['content'],
    links: [{ title: 'Homepage', url: 'https://www.sanity.io' }],
    serviceType: 'graphql',
    introspectionType: 'graphql',
    createId: value('sanity'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'sanity'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'Sanity'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Sanity'
            },
            endpoint: {
                title: 'GraphQL Endpoint URL',
                type: 'string'
            },
            authentication: {
                type: 'object',
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                properties: {
                    token: {
                        title: 'API Token',
                        type: 'string',
                        '@sensitive': true
                    }
                }
            }
        },
        required: ['title', 'id', 'namespace', 'endpoint']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'endpoint', 'apiToken', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            endpoint: {
                widget: 'singleLineText',
                instructions: 'Can be found by running `sanity graphql list`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'Leave blank for unauthenticated access. Tokens can be created under the API tab in your Sanity project.'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? 'Sanity',
            namespace: formData.namespace ?? 'Sanity',
            provider: 'sanity',
            serviceType: 'graphql',
            authenticationType: formData?.authentication?.token ? 'bearer' : 'none',
            options: {
                endpoint: formData.endpoint
            }
        };
        if (!formData?.authentication?.token) {
            return config;
        }
        return {
            ...config,
            authenticationType: 'bearer',
            authentication: {
                type: 'bearer',
                token: fixObfuscatedProp(formData.authentication?.token)
            }
        };
    },
    isCompleteConfig: (config) => hasValidEndpoint(config),
    getHealthCheck: getHealthCheck({ checkName: 'graphqlIntrospection' })
};
export default serviceProvider;
